import type { ClassValue } from 'clsx'
import type { CSSProperties } from 'react'
import type {
	DropdownOption,
	SRT_Column,
	SRT_Header,
	SRT_RowData,
	SRT_TableInstance,
} from '../types'

import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export const parseFromValuesOrFunc = <T, U>(
	fn: ((arg: U) => T) | T | undefined,
	arg: U
): T | undefined => (fn instanceof Function ? fn(arg) : fn)

export const getValueAndLabel = (
	option?: DropdownOption | null
): { label: string; value: string } => {
	let label = ''
	let value = ''
	if (option) {
		if (typeof option !== 'object') {
			label = option
			value = option
		} else {
			label = option.label ?? option.value
			value = option.value ?? label
		}
	}

	return { label, value }
}

export function getCommonPinningStyles<T extends SRT_RowData>(
	column: SRT_Column<T>,
	layoutMode?: string
): CSSProperties {
	const isPinned = column.getIsPinned()
	/* 	const isLastLeftPinnedColumn =
		isPinned === 'left' && column.getIsLastColumn('left')
	const isFirstRightPinnedColumn =
		isPinned === 'right' && column.getIsFirstColumn('right')
 */
	const width =
		layoutMode === 'semantic'
			? column.getSize()
			: column.getSize() === Number.MAX_SAFE_INTEGER
				? 'auto'
				: column.getSize()

	return {
		/* 	boxShadow: isLastLeftPinnedColumn
			? '-4px 0 4px -4px gray inset'
			: isFirstRightPinnedColumn
				? '4px 0 4px -4px gray inset'
				: undefined, */
		left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
		right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
		opacity: 1,
		position: isPinned ? 'sticky' : 'relative',
		width: width,
		minWidth: width,
		maxWidth: width,
		zIndex: isPinned ? 1 : 0,
	}
}
export function getHeaderPinningStyles<T extends SRT_RowData>(
	header: SRT_Header<T>,
	{
		isPinned,
		left,
		right,
		layoutMode,
	}: {
		isPinned: boolean | 'left' | 'right'
		left: number
		right: number
		layoutMode?: string
	}
): CSSProperties {
	//const isPinned = header.column.getIsPinned();
	const width =
		layoutMode === 'semantic'
			? header.getSize()
			: header.getSize() === Number.MAX_SAFE_INTEGER
				? 'auto'
				: header.getSize()

	return {
		right: isPinned === 'right' ? `${right}px` : undefined,
		left: isPinned === 'left' ? `${left}px` : undefined,
		opacity: 1,
		position: isPinned ? 'sticky' : 'relative',
		width: width,
		minWidth: width,
		maxWidth: width,
		zIndex: isPinned ? 20 : 'auto',
	}
}

export function getHeaders<T extends SRT_RowData>(table: SRT_TableInstance<T>) {
	const grouped = table.getLeafHeaders().reduce(
		(acc, header) => {
			if (acc[header.depth]?.length) {
				acc[header.depth].push(header)
			} else {
				acc[header.depth] = [header]
			}

			return acc
		},
		{} as Record<number, SRT_Header<T>[]>
	)

	return Object.values(grouped).map((headers) => {
		const hasSubheaders = (header: SRT_Header<T>) => header.subHeaders?.length

		return headers.map((header) => {
			const isPinned = hasSubheaders(header)
				? header.subHeaders.map((h) => h.column.getIsPinned())[0]
				: header.column.getIsPinned()
			const right = hasSubheaders(header)
				? Math.min(...header.subHeaders.map((h) => h.column.getAfter('right')))
				: header.column.getAfter('right')
			const left = hasSubheaders(header)
				? Math.min(...header.subHeaders.map((h) => h.column.getStart('left')))
				: header.column.getStart('left')

			return {
				id: header.id,
				depth: header.depth,
				isPinned,
				header,
				left,
				right,
			}
		})
	})
}
